import React, { useEffect, useState } from "react";
import "./ProfileModal.scss";
import { Modal, notification, Spin, Tabs } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { TProfileModal } from "@utils/Interfaces";
import AboutEdit from "./AboutEdit";
import ExperienceEdit from "./ExperienceEdit";
import HardSkillEdit from "./HardSkillEdit";
import LanguageEdit from "./LanguageEdit";
import PersonalEdit from "./PersonalEdit";
import CertificateEdit from "./CertificateEdit";
import EducationEdit from "./EducationEdit";
import AuthenticateApi from "services/AuthenticateApi";
import Helpers from "@utils/Helpers";

const ProfileModal: React.FC<TProfileModal> = ({
  isOpen,
  cancel,
  activeTab,
  setActiveTab,
  userProfile,
  submit,
  modalLoading,
}) => {
  const [data, setData] = useState({ ...userProfile });
  const [editedFields, setEditedFields] = useState<{}>({});
  const [avatarFile, setAvatarFile] = useState<any>(null);
  const [addExpForm, setAddExpForm] = useState<any>(null);
  const [addEduForm, setAddEduForm] = useState<any>(null);
  const [isAddFormIntoEditedField, setIsAddFormIntoEditedField] =
    useState(false);

  const handleChangeValue = (value: any, key: string) => {
    setData({ ...data, [key]: value });
    setEditedFields({
      ...editedFields,
      [key]: value,
    });
  };

  useEffect(() => {
    if (isAddFormIntoEditedField) {
      const currentExp: any = data.experience ? [...data.experience] : [];
      currentExp.push(addExpForm);
      const payload = { ...editedFields, experience: currentExp };
      submit(payload);
      setEditedFields({});
      setAddExpForm(null);
    }
  }, [isAddFormIntoEditedField]);

  const tabs = [
    {
      label: "Personal Information",
      key: "1",
      children: (
        <PersonalEdit
          data={data}
          setAvatarFile={setAvatarFile}
          handleChangeValue={handleChangeValue}
        />
      ),
    },
    {
      label: "About",
      key: "2",
      children: <AboutEdit data={data} handleChangeValue={handleChangeValue} />,
    },
    {
      label: "Experience",
      key: "3",
      children: (
        <ExperienceEdit
          data={data}
          handleChangeValue={handleChangeValue}
          setAddExpForm={setAddExpForm}
        />
      ),
    },
    {
      label: "Skills",
      key: "4",
      children: (
        <HardSkillEdit
          data={data}
          handleChangeValue={handleChangeValue}
          setAddEduForm={setAddEduForm}
        />
      ),
    },
    {
      label: "Education",
      key: "5",
      children: (
        <EducationEdit data={data} handleChangeValue={handleChangeValue} />
      ),
    },
    {
      label: "Certificates",
      key: "6",
      children: (
        <CertificateEdit data={data} handleChangeValue={handleChangeValue} />
      ),
    },
    {
      label: "Languages",
      key: "7",
      children: (
        <LanguageEdit data={data} handleChangeValue={handleChangeValue} />
      ),
    },
  ];

  const handleUpdateProfile = async () => {
    if (addExpForm != null) {
      setIsAddFormIntoEditedField(true);
    } else {
      if (avatarFile) {
        const upload: any = await handleReplaceAvatarInS3();
        if (upload !== "error") {
          setData({ ...data, avatar: upload.avatar_url });
          const newEditedFields: any = { ...editedFields };
          newEditedFields.avatar = upload.avatar_url;
          submit(newEditedFields);
          setEditedFields({});
        }
      } else {
        submit(editedFields);
        setEditedFields({});
        setAddExpForm(null);
      }
    }
  };

  const handleReplaceAvatarInS3 = async () => {
    const upload: any = await storeAvatarToS3(avatarFile);
    if (upload && upload.status == 200) {
      const oldAvatarFileName: any = data.avatar;
      await handleRemoveAvatarFromS3(oldAvatarFileName);
      return upload.response;
    }
    return "error";
  };

  const storeAvatarToS3 = async (avatarFile: File) => {
    if (avatarFile.size > 5000000) {
      Helpers.notification.warning("Image size must be less than 5MB");
      return;
    }
    const formData = new FormData();
    formData.append("file", avatarFile);
    try {
      const response = await AuthenticateApi.UploadImageToS3(formData);
      return response;
    } catch (error: any) {
      console.error("Upload: ", error);
      Helpers.notification.error(`file upload failed.`);
    }
  };

  const handleRemoveAvatarFromS3 = async (avatarUrl: string) => {
    try {
      const response = await AuthenticateApi.DeleteImageFromS3(avatarUrl);
      return response;
    } catch (error) {
      console.error(error);
      Helpers.notification.error("Something wrong in server");
    }
  };

  return (
    <Modal
      title={"Profile"}
      className="ProfileModal"
      open={isOpen}
      onCancel={cancel}
      onOk={handleUpdateProfile}
      width={"65vw"}
    >
      <div className="modal-body-wrap mt-4">
        {modalLoading && (
          <div className="modal-loading">
            <Spin
              indicator={<LoadingOutlined spin />}
              style={{ color: "#5ac6c6" }}
              size="large"
            />
          </div>
        )}
        <Tabs
          defaultActiveKey={tabs[0].key}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          tabPosition={"left"}
          items={tabs}
          style={{ height: "100%" }}
        />
      </div>
    </Modal>
  );
};

export default ProfileModal;
